import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  Output,
  ViewChild,
} from '@angular/core';

import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  ControlValueAccessor,
  FormControl,
  FormGroup,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { DateHelperService } from '../services/date-helper.service';
import { DateTime } from 'luxon';
import { InfoTooltipComponent } from './info-tooltip.component';

@Component({
  selector: 'app-datetime-input',
  imports: [
    MatDatepickerModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatTooltipModule,
    ReactiveFormsModule,
    FormsModule,
    InfoTooltipComponent,
  ],
  template: `
    <!-- A date and its time are one value, so they sit closer together than two independent
         fields would. The wider gap-4 stays for the time-only case, where there is no pair. -->
    <div class="flex w-full" [class.gap-4]="hideDate" [class.gap-2]="!hideDate">
      @if (!hideDate) {
        <!-- dd-compact-field opts these two inner fields into the compact Figma geometry. -->
        <mat-form-field class="dd-compact-field flex-1" [class.mat-form-field-invalid]="required && !datePart">
          <!-- With both halves on screen the field's own label ("Aankomsttijd") belongs to the
               TIME box; naming the date box after it puts "Aankomsttijd" above DD-MM-YYYY.
               Unless the time box is not showing a label at all (hideTimelabel, as date-range
               sets for a single-day range) — then the date box keeps the field's label, or the
               field would render with no label anywhere. -->
          <mat-label
            >{{ hideTimelabel || hideTime ? label : 'Datum' }}
            <app-info-tooltip [tooltip]="info"></app-info-tooltip>
          </mat-label>
          <input
            matInput
            #dateInput
            #date="ngModel"
            name="date"
            [readonly]="isEmbedded"
            [disabled]="isDisabled"
            [required]="required"
            [ngModelOptions]="{ standalone: true }"
            data-cy="datetime-input-date"
            [(ngModel)]="datePart"
            (ngModelChange)="onDateChange($event)"
            [matDatepicker]="picker"
            (click)="openPicker(dateInput, picker)" />
          <mat-hint>DD-MM-YYYY</mat-hint>
          <mat-datepicker-toggle matIconSuffix [for]="picker"></mat-datepicker-toggle>
          <mat-datepicker #picker [startAt]="startDate"></mat-datepicker>
          @if (date.errors) {
            <mat-error>Voer een geldige datum in. </mat-error>
          }
        </mat-form-field>
      }
      <!-- With a date beside it the time gets a fixed track instead of an equal flex share:
           both were flex-1, which split the row down the middle and clipped "09-09-2026" to
           "09-09-20" in a half-width column. A time needs ~92px, a date ~80px plus its calendar
           button, so the date takes the remainder. Time-only keeps the old full-width behaviour. -->
      @if (!hideTime) {
        <mat-form-field
          class="dd-compact-field min-w-[73px] pb-2"
          [class.flex-1]="hideDate"
          [style.flex]="hideDate ? null : '0 0 104px'"
          [class.mat-form-field-invalid]="required && !timePart && !isDisabled">
          @if (!hideTimelabel) {
            <mat-label>{{ label }}</mat-label>
          }
          <input
            type="time"
            step="900"
            matInput
            [class.dd-time-empty]="!timePart"
            [(ngModel)]="timePart"
            [required]="isDisabled ? false : required"
            [ngModelOptions]="{ standalone: true }"
            (ngModelChange)="onTimeChange($event)"
            data-cy="datetime-input-time"
            #timeInput
            #time="ngModel"
            name="time"
            class="text-center"
            [disabled]="isDisabled" />
          <!-- Opt-in, and inert for every field that does not set it: a short badge inside the
             time box, for a caller that needs to say something ABOUT this time rather than beside
             it. The route uses it for the day a stop landed on. -->
          @if (timeBadge) {
            <!-- Dot-free class names below on purpose: Angular splits a bound property on '.',
               so [class.pr-1.5] would bind the class "pr-1" and drop the .5 silently. -->
            <span
              matSuffix
              class="dd-time-badge mr-1 inline-flex shrink-0 items-center gap-0.5 rounded bg-warning bg-opacity-25 py-0.5 pl-1.5 text-xs font-medium leading-none text-default"
              [class.pr-2]="!timeBadgeDismissLabel || isDisabled"
              [class.pr-1]="timeBadgeDismissLabel && !isDisabled">
              {{ timeBadge }}
              <!-- No cross on a disabled field. The input next to it is already read-only, and a
                 working button beside it would be a lie: pressing it rewrites the route. -->
              @if (timeBadgeDismissLabel && !isDisabled) {
                <button
                  type="button"
                  class="flex h-4 w-4 items-center justify-center rounded-full hover:bg-warning hover:bg-opacity-25"
                  (click)="$event.stopPropagation(); timeBadgeDismiss.emit()"
                  [attr.aria-label]="timeBadgeDismissLabel"
                  [matTooltip]="timeBadgeDismissLabel">
                  <mat-icon class="!h-3 !w-3 !text-xs !leading-3 text-default">close</mat-icon>
                </button>
              }
            </span>
          }
          <span matSuffix class="dd-clear-suffix">
            <ng-content select="[datetimeTrailingAction]"></ng-content>
          </span>
          @if (time.errors) {
            <mat-error>Voer een geldige tijd in. </mat-error>
          }
        </mat-form-field>
      }
    </div>
  `,
  styles: ``,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DatetimeInputComponent),
      multi: true,
    },
  ],
})
export class DatetimeInputComponent implements ControlValueAccessor {
  @ViewChild('dateInput') private dateInput?: ElementRef<HTMLInputElement>;
  @ViewChild('timeInput') private timeInput?: ElementRef<HTMLInputElement>;

  @Input() label: string = 'Datum';
  @Input() hideTimelabel = false;
  /** Short text shown inside the time box, e.g. "+1 dag". Nothing renders without it. */
  @Input() timeBadge: string | null = null;
  /** Accessible name for the badge's dismiss button. Without it the badge has no cross. */
  @Input() timeBadgeDismissLabel: string | null = null;
  @Output() timeBadgeDismiss = new EventEmitter<void>();
  @Input() hideDate = false;
  /** Date only: hides the time box and writes a date-only value (YYYY-MM-DD). */
  @Input() hideTime = false;
  @Input() disabled = false;
  @Input() required = false;
  @Input() info: string;
  isEmbedded = !!window.flutter_inappwebview;

  formGroup = new FormGroup({
    date: new FormControl(''),
    time: new FormControl(''),
  });

  @Input() set dateTime(value: string | null) {
    if (value !== this._dateTime) {
      this._dateTime = value ?? '';
      this.parseDateTime(value);
    }
  }

  get dateTime(): string {
    return this._dateTime;
  }

  @Input() isDisabled = false;
  @Output() dateTimeChange = new EventEmitter<string>();

  private _dateTime = '';

  private onTouchedCallback = () => {};
  private onChangeCallback: (_: any) => void = () => {};

  datePart = null;
  timePart = null;
  @Input() startDate;

  writeValue(value: any) {
    this.dateTime = value;
  }

  registerOnChange(fn: any) {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouchedCallback = fn;
  }

  private parseDateTime(value: string | null) {
    if (value === null || !value) {
      this.datePart = null;
      this.timePart = null;
      return;
    }

    const { date, time } = DateHelperService.dateTimeToDateAndTime(value);
    this.datePart = date;
    this.timePart = time;
  }

  onDateChange(date: DateTime) {
    this.updateDateTime(date?.toISODate() ?? null, this.hideTime ? null : this.timePart);
  }

  onTimeChange(time: string) {
    this.updateDateTime(this.datePart, time);
  }

  private updateDateTime(date: string, time: string) {
    // Time-only fields (hideDate) have no date input, so `date` arrives as null and
    // `dateAndTimeToDateTime` would drop the entered time. Fall back to the date the
    // control already holds, or today, so the time still produces a complete value.
    if (this.hideDate && date == null) {
      const existing = DateHelperService.dateTimeToDateAndTime(this._dateTime);
      date = existing.date ?? DateHelperService.dateObjectToDateString(new Date());
    }

    const newTimeDate = DateHelperService.dateAndTimeToDateTime(date, time);
    if (newTimeDate) {
      this._dateTime = newTimeDate;
      this.onChangeCallback(this._dateTime);
      this.dateTimeChange.emit(this._dateTime);
      this.onTouchedCallback();
    }
  }

  focus(): void {
    (this.hideDate ? this.timeInput : this.dateInput)?.nativeElement.focus();
  }

  // Open picker while maintaining input focus
  openPicker(input: HTMLInputElement, picker: any): void {
    picker.open();
    // Maintain focus on input after picker opens
    setTimeout(() => input.focus(), 32);
  }
}
